import React from "react";
import { CATEGORY_LABEL, daysBetween, fmtDate, parseDate } from "./utils";
import { SunIcon, SnowIcon, BuildingIcon } from "./icons";

function WindowDetailCard({ kind, start, end, now }) {
  const s = parseDate(start);
  const e = parseDate(end);
  const title = kind === "summer" ? "Summer window" : "Winter window";
  // Sun / Snow have their stroke colours baked in now — no style prop.
  const iconNode = kind === "summer" ? <SunIcon /> : <SnowIcon />;

  if (!s || !e) {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-4">
        <div
          className="flex items-center gap-2 text-sm font-semibold text-[#314158]"
          style={{ letterSpacing: "-0.15px" }}
        >
          {iconNode}
          <span>{title}</span>
        </div>
        <div className="pt-3 text-sm text-slate-400">Not scheduled</div>
      </div>
    );
  }

  const span = daysBetween(s, e) + 1;
  let pillNode, dayLine;
  if (s <= now && now <= e) {
    const left = Math.max(0, daysBetween(now, e));
    pillNode = (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
        <span className="w-1.5 h-1.5 rounded-full bg-current" />
        Open
      </span>
    );
    dayLine = `${left} day${left === 1 ? "" : "s"} left`;
  } else if (s > now) {
    const days = daysBetween(now, s);
    pillNode = (
      <span
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
        style={{ background: "#FEF3C6", color: "#BB4D00" }}
      >
        Opens in {days}d
      </span>
    );
    dayLine = `${span} day window`;
  } else {
    pillNode = (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
        Closed
      </span>
    );
    dayLine = `${span} day window`;
  }

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-4">
      <div
        className="flex items-center gap-2 text-sm font-semibold text-[#314158]"
        style={{ letterSpacing: "-0.15px" }}
      >
        {iconNode}
        <span>{title}</span>
      </div>
      <div className="pt-3 flex flex-col gap-2">
        <div
          className="text-sm font-medium text-[#0F172B]"
          style={{ letterSpacing: "-0.15px" }}
        >
          {fmtDate(s)} – {fmtDate(e)}
        </div>
        <div>{pillNode}</div>
        <div className="text-xs text-[#62748E]">{dayLine}</div>
      </div>
    </div>
  );
}

function AssociationCard({ row }) {
  const Row = ({ k, v }) => (
    <div className="flex items-center justify-between">
      <span className="text-xs text-[#62748E]">{k}</span>
      <span className="text-xs text-[#0F172B] font-medium">{v}</span>
    </div>
  );
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-4">
      <div
        className="flex items-center gap-2 text-sm font-semibold text-[#314158]"
        style={{ letterSpacing: "-0.15px" }}
      >
        {/* BuildingIcon is a pre-rendered JSX element (object), not a
            component — render it with {} not <... />. */}
        {BuildingIcon}
        <span>Association details</span>
      </div>
      <div className="pt-3 flex flex-col gap-2">
        <Row k="Confederation" v={row.confederation || "—"} />
        <Row k="Season" v={row.season || "—"} />
        <Row k="Category" v={CATEGORY_LABEL[row.category] || "Men's Pro"} />
      </div>
    </div>
  );
}

// Three-card grid shown inside an expanded table row: Summer window /
// Winter window / Association details. Window labels are read from
// `window_1_label` / `window_2_label` so we can swap slots on countries
// whose CSV records the mid-year window in the second slot.
export default function DetailPanel({ row, now }) {
  let summerStart, summerEnd, winterStart, winterEnd;
  if (row.window_1_label === "summer") {
    summerStart = row.window_1_start;
    summerEnd = row.window_1_end;
  } else if (row.window_1_label === "winter") {
    winterStart = row.window_1_start;
    winterEnd = row.window_1_end;
  }
  if (row.window_2_label === "summer") {
    summerStart = row.window_2_start;
    summerEnd = row.window_2_end;
  } else if (row.window_2_label === "winter") {
    winterStart = row.window_2_start;
    winterEnd = row.window_2_end;
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 py-2">
      <WindowDetailCard kind="summer" start={summerStart} end={summerEnd} now={now} />
      <WindowDetailCard kind="winter" start={winterStart} end={winterEnd} now={now} />
      <AssociationCard row={row} />
    </div>
  );
}
